// NEW_FILE_JUSTIFIED: Email row component for Gmail-style email list — no existing component handles table-based email display
import { useCallback } from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Paperclip, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IEmailRowProps, IDraftRowProps } from '@/@types/gmail';

// ============================================================================
// Pure helpers
// ============================================================================

/**
 * Format email timestamp:
 * - Less than 24 hours: show time (HH:MM)
 * - More than 24 hours: show date (DD Mon)
 */
function formatEmailTime(timestampMs: number): string {
  const date = new Date(timestampMs);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

/**
 * Decode HTML entities that Gmail API returns in snippets.
 * Uses a textarea element for reliable browser-native decoding.
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

// ============================================================================
// EmailRow Component
// ============================================================================

export function EmailRow({ email, isSelected, onSelect, onClick, onCreateLead }: IEmailRowProps) {
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onSelect(email.id, checked);
    },
    [email.id, onSelect]
  );

  const handleRowClick = useCallback(() => {
    onClick(email.threadId);
  }, [email.threadId, onClick]);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick]
  );

  const isUnread = !email.isRead;

  return (
    <TableRow
      className={cn(
        'group cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'bg-muted/30'
      )}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      aria-label={`Email from ${email.fromName}: ${email.subject || '(no subject)'}`}
    >
      {/* Checkbox */}
      <TableCell className="w-10 px-3" onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          aria-label={`Select email from ${email.fromName}`}
        />
      </TableCell>

      {/* Sender / Participants */}
      <TableCell className="w-40 px-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <span
            className={cn(
              'text-sm truncate',
              isUnread ? 'font-bold text-foreground' : 'text-muted-foreground'
            )}
          >
            {email.threadParticipants ?? email.fromName}
          </span>
          {email.threadMessageCount !== undefined && email.threadMessageCount > 1 && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {email.threadMessageCount}
            </span>
          )}
        </div>
      </TableCell>

      {/* Subject + Body Preview */}
      <TableCell className="px-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              'text-sm shrink-0 max-w-[50%] truncate',
              isUnread ? 'font-bold text-foreground' : 'text-foreground'
            )}
          >
            {email.subject || '(no subject)'}
          </span>
          {email.snippet && (
            <span className="text-sm text-muted-foreground truncate min-w-0 flex-1">
              — {decodeHtmlEntities(email.snippet)}
            </span>
          )}
          {email.hasAttachments && (
            <Paperclip
              size={14}
              className="text-muted-foreground shrink-0"
              aria-label="Has attachments"
              role="img"
            />
          )}
        </div>
      </TableCell>

      {/* Date/Time */}
      <TableCell className="w-24 px-3 text-right">
        <span
          className={cn(
            'text-xs whitespace-nowrap',
            isUnread ? 'font-bold text-foreground' : 'text-muted-foreground'
          )}
        >
          {formatEmailTime(email.internalDate)}
        </span>
      </TableCell>

      {/* Actions — visible on hover */}
      <TableCell className="w-10 px-1">
        {onCreateLead && (
          <button
            type="button"
            title="Create lead from this email"
            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-muted text-muted-foreground hover:text-foreground transition-all"
            onClick={e => {
              e.stopPropagation();
              const nameMatch = email.from.match(/^([^<]+)</);
              const name = nameMatch ? nameMatch[1].trim() : email.fromName;
              const emailAddr = email.from.match(/<([^>]+)>/)?.[1] ?? email.from;
              onCreateLead(name, emailAddr, email.subject, email.bodyText ?? email.snippet);
            }}
          >
            <UserPlus size={14} />
          </button>
        )}
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// DraftRow Component
// ============================================================================

export function DraftRow({ draft, isSelected, onSelect, onClick }: IDraftRowProps) {
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      onSelect(draft.id, checked);
    },
    [draft.id, onSelect]
  );

  const handleRowClick = useCallback(() => {
    onClick(draft.id);
  }, [draft.id, onClick]);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick]
  );

  return (
    <TableRow
      className={cn(
        'cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isSelected && 'bg-muted/30'
      )}
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      aria-label={`Draft: ${draft.subject || '(no subject)'}`}
    >
      {/* Checkbox */}
      <TableCell className="w-10 px-3" onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          aria-label={`Select draft: ${draft.subject || '(no subject)'}`}
        />
      </TableCell>

      {/* Recipients */}
      <TableCell className="w-40 px-3">
        <span className="text-sm text-muted-foreground truncate block">
          {draft.to.length > 0 ? draft.to.join(', ') : '(no recipients)'}
        </span>
      </TableCell>

      {/* Subject + Body Preview */}
      <TableCell className="px-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm text-destructive shrink-0">Draft</span>
          <span className="text-sm text-foreground truncate">
            {draft.subject || '(no subject)'}
          </span>
        </div>
      </TableCell>

      {/* Updated Time */}
      <TableCell className="w-24 px-3 text-right">
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatEmailTime(draft.updatedAt)}
        </span>
      </TableCell>
    </TableRow>
  );
}
